/**
 * Chrome-specific fixes for CMR document rendering
 * Addresses the stretched header issue in the complete-cmr page
 */

/**
 * Detect if browser is Chrome
 */
export const isChrome = () => {
  const isChromium = window.chrome;
  const winNav = window.navigator;
  const vendorName = winNav.vendor;
  const isOpera = typeof window.opr !== "undefined";
  const isIEedge = winNav.userAgent.indexOf("Edg") > -1;
  const isIOSChrome = winNav.userAgent.match("CriOS");

  if (isIOSChrome) {
    return true;
  } else if (
    isChromium !== null &&
    typeof isChromium !== "undefined" &&
    vendorName === "Google Inc." &&
    isOpera === false &&
    isIEedge === false
  ) {
    return true;
  } else {
    return false;
  }
};

/**
 * Apply Chrome-specific fixes to CMR document
 */
export const applyChromeDocumentFixes = (containerElement) => {
  if (!isChrome()) return;

  // Add Chrome-specific class for CSS targeting
  if (containerElement) {
    containerElement.classList.add('chrome-cmr-document');
  }

  // Fix transform scale rendering issues
  const scaleElements = document.querySelectorAll('[style*="transform: scale"]');
  scaleElements.forEach(element => {
    // Force GPU acceleration
    const currentTransform = element.style.transform;
    if (!currentTransform.includes('translateZ')) {
      element.style.transform = `${currentTransform} translateZ(0)`;
    }
    element.style.willChange = 'transform';
    element.style.backfaceVisibility = 'hidden';
    element.style.perspective = '1000px';
  });

  // Fix font rendering at different scales
  const textElements = document.querySelectorAll('[style*="fontSize"]');
  textElements.forEach(element => {
    element.style.webkitFontSmoothing = 'antialiased';
    element.style.textRendering = 'optimizeLegibility';
  });

  // Fix CMR header cells specifically
  const headerCells = document.querySelectorAll('[style*="ROAD CONSIGNMENT NOTE"]');
  headerCells.forEach(cell => {
    if (cell.parentElement) {
      cell.parentElement.style.display = 'flex';
      cell.parentElement.style.alignItems = 'center';
      cell.parentElement.style.justifyContent = 'center';
    }
  });
};

/**
 * Calculate optimal scale for Chrome
 */
export const calculateChromeScale = (fitScale, zoomScale) => {
  if (!isChrome()) {
    return { scale: fitScale * zoomScale, useZoom: false };
  }

  // For Chrome, prefer using zoom CSS property for better rendering
  const totalScale = fitScale * zoomScale;

  // Chrome handles zoom better than transform for text rendering
  if (totalScale >= 0.5 && totalScale <= 2) {
    return { scale: totalScale, useZoom: true };
  }

  // Fall back to transform for extreme scales
  return { scale: totalScale, useZoom: false };
};

/**
 * Apply scale with Chrome optimization
 */
export const applyOptimizedScale = (element, fitScale, zoomScale) => {
  if (!element) return;

  const { scale, useZoom } = calculateChromeScale(fitScale, zoomScale);

  if (isChrome() && useZoom) {
    // Use zoom for Chrome (better text rendering)
    element.style.zoom = scale;
    element.style.transform = 'none';
    element.style.transformOrigin = 'top left';
  } else {
    // Use transform for other browsers or extreme scales
    element.style.zoom = '';
    element.style.transform = `scale(${scale})`;
    element.style.transformOrigin = 'top left';
  }
};

/**
 * Fix Chrome print rendering
 */
export const fixChromePrint = () => {
  if (!isChrome()) return;

  // Add print-specific styles for Chrome
  const style = document.createElement('style');
  style.textContent = `
    @media print {
      .chrome-cmr-document {
        zoom: 1 !important;
        transform: none !important;
      }

      .chrome-cmr-document * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }

      [style*="fontSize"] {
        -webkit-font-smoothing: antialiased !important;
      }
    }
  `;
  document.head.appendChild(style);
};

/**
 * Initialize Chrome CMR fixes
 */
export const initializeChromeCMRFixes = () => {
  if (!isChrome()) return;

  // Apply fixes when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      applyChromeDocumentFixes(document.body);
      fixChromePrint();
    });
  } else {
    applyChromeDocumentFixes(document.body);
    fixChromePrint();
  }

  // Monitor for dynamic content changes
  const observer = new MutationObserver((mutations) => {
    mutations.forEach(mutation => {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) { // Element node
            // Check if it's a CMR document element
            if (node.querySelector && node.querySelector('[style*="210mm"]')) {
              applyChromeDocumentFixes(node);
            }
          }
        });
      }
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
};